import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { EMPTY_ROLE_PERMISSION_DIFF, RolePermissionDiff } from 'src/app/shared/utils/role-permission-diff.util';

/**
 * Sticky save bar for the Roles-tab detail pane (DASH-3179, part 6/9; design-spec §5.6, §8).
 *
 * A dumb presentational footer with three states, driven entirely by inputs so the container owns the
 * write and the dirty model:
 * - **clean** — muted "No unsaved changes." status; Save blocked.
 * - **dirty** — amber bar with an Unsaved badge, the `+added` / `−removed` verb diff across N objects,
 *   Discard (ghost) and Save role (primary). Save is actionable only when `canSave` (a valid, non-system role).
 * - **saving** — the in-flight state; both actions suppressed.
 *
 * The diff summary is `aria-live="polite"` so a screen reader hears the change as the admin edits (§6 SaveBar).
 * The `Review` link emits {@link review} so the container switches the matrix to the Changed filter (part 7/9).
 */
@Component({
  standalone: false,
  selector: 'app-role-permission-save-bar',
  templateUrl: './role-permission-save-bar.component.html',
  styleUrls: ['./role-permission-save-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePermissionSaveBarComponent implements OnChanges {
  /** The unsaved change summary; an empty diff renders the clean state. */
  @Input() diff: RolePermissionDiff = EMPTY_ROLE_PERMISSION_DIFF;

  /** True while a save is in flight — shows the saving state and suppresses both actions. */
  @Input() saving = false;

  /** Whether the Save action is actionable (role is dirty, valid and not a protected system role). */
  @Input() canSave = false;

  /** Full catalogue size, surfaced in the saving message and Save's accessible name (§8 whole-catalogue save). */
  @Input() objectCount = 0;

  /**
   * True when the role's name or description diverges from its last-saved baseline, with no permission
   * change (DASH-3179 part 7/9). Renders the dirty (amber) bar with a "Role details changed" summary so a
   * pure rename is still saveable — the `+N` / `−N` verb diff stays permission-only.
   */
  @Input() metadataDirty = false;

  /**
   * True when the selected role is a brand-new unsaved draft (added or cloned, no server id yet). A fresh
   * draft has no diff against its own baseline and no metadata divergence, so it must still render the
   * actionable (amber) bar — otherwise Save stays blocked until an unrelated edit creates a diff, even
   * though the role is already saveable (DASH-3179; clone-then-save regression).
   */
  @Input() isNew = false;

  @Output() save = new EventEmitter<void>();
  @Output() discard = new EventEmitter<void>();

  /** Requests the matrix switch to the `Changed` filter so only the edited rows show (design-spec §5.6). */
  @Output() review = new EventEmitter<void>();

  /** True when the role has unsaved changes or is a new draft — computed on input change so the template needs no call. */
  dirty = false;

  ngOnChanges(): void {
    this.dirty = this.diff.objectCount > 0 || this.metadataDirty || this.isNew;
  }
}
